// React import not needed with automatic JSX runtime
import { useTranslation } from 'react-i18next';
import type { CheckUrlResponse } from '../services/api';
import { formatDate, formatDateTime } from '../utils/date';

interface ResultsProps {
  result: CheckUrlResponse;
}

export function Results({ result }: ResultsProps) {
  const { t } = useTranslation();

  const verdictClasses = {
    Safe: 'verdict-badge--safe',
    Suspicious: 'verdict-badge--suspicious',
    Unknown: 'verdict-badge--unknown',
  };

  const verdictClass = verdictClasses[result.verdict];

  return (
    <div className="card-compact">
      <div className={`verdict-badge ${verdictClass} mb-4`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t(`verdict.${result.verdict}`)}</h2>
            <p className="text-sm mt-1">{t(`guidance.${result.verdict.toLowerCase()}`)}</p>
          </div>
          <div className="text-2xl font-bold">{result.confidence}%</div>
        </div>
      </div>

      <div className="text-sm text-gray-600 mb-4">
        <strong>URL:</strong> {result.normalizedUrl}
      </div>

      {result.evidence && result.evidence.length > 0 && (
        <div className="mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">{t('evidence.title')}</h3>
          <ul className="list-disc list-inside text-sm text-gray-700">
            {result.evidence.map((item, idx) => {
              // Check if it's a translation key or contains dynamic data
              if (item.startsWith('registry.match:')) {
                const entityName = item.substring('registry.match:'.length);
                return <li key={idx}>{t('evidence.registryMatch', { name: entityName })}</li>;
              }
              // Try to translate as key, fallback to raw text if not found
              const translated = t(item, { defaultValue: item });
              return <li key={idx}>{translated}</li>;
            })}
          </ul>
        </div>
      )}

      {result.sources && result.sources.length > 0 && (
        <div>
          <h3 className="section-heading">{t('evidence.title')}</h3>
          <ul className="text-sm text-gray-700">
            {result.sources.map((source, idx) => (
              <li key={idx} className="mb-1">
                <a
                  href={source.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-external"
                >
                  {source.sourceName}
                </a>
                {source.lastUpdatedAt && (
                  <span className="text-gray-500 ml-2">
                    ({formatDate(source.lastUpdatedAt)})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.brandImpersonation && result.brandImpersonation.classification !== 'OK' && (
        <div className="mt-4 p-4 border-l-4 border-red-500 bg-red-50">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-semibold text-red-800">
                {t(`brandImpersonation.classification.${result.brandImpersonation.classification}`)}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{result.brandImpersonation.reason}</p>
                {result.brandImpersonation.closestTrustedDomain && (
                  <p className="mt-2">
                    <strong>{t('brandImpersonation.suggestion')}:</strong>{' '}
                    <a 
                      href={`https://${result.brandImpersonation.closestTrustedDomain}`}
                      className="underline hover:text-red-900"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {result.brandImpersonation.closestTrustedDomain}
                    </a>
                  </p>
                )}
              </div>
              <div className="mt-2 text-xs text-red-600">
                {t('brandImpersonation.score', { score: result.brandImpersonation.score })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        Checked: {formatDateTime(result.checkedAt)}
      </div>
    </div>
  );
}
